import React, { useState } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  useToast,
  Spinner,
  Grid,
  Input,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { DollarSign, Coins, CreditCard, Info } from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerButton from '../components/ui/PokerButton';
import { auth } from '../firebase';
import stripeService from '../services/stripeService';
import iapService from '../services/iapService';
import { usePaymentsHold } from '../services/paymentsHoldService';
import { Capacitor } from '@capacitor/core';

// Preset purchase amounts
const PRESET_AMOUNTS = [
  { usd: 10, coins: 10000, cash: 10 },
  { usd: 25, coins: 25000, cash: 25 },
  { usd: 50, coins: 50000, cash: 50 },
  { usd: 100, coins: 100000, cash: 100 },
];

const BuyCoinsPage: React.FC = () => {
  const [selectedPreset, setSelectedPreset] = useState<number | null>(1); // Default 25 USD (index 1)
  const [customAmount, setCustomAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const paymentsHold = usePaymentsHold();
  // Apple App Store purchases run on Apple's rails, not the card processor,
  // so the hold only touches them when the admin explicitly says so.
  const purchasesPaused = paymentsHold.enabled && (iapService.shouldUseIAP() ? paymentsHold.blockIap : paymentsHold.blockPurchases);
  // Store apps never see the crypto rail: iPhone buys through Apple, the
  // Android store app shows purchases as unavailable (no link out). Only a
  // real web browser gets the temporary crypto checkout.
  const isNativeApp = Capacitor.isNativePlatform();
  const androidStoreApp = isNativeApp && !iapService.shouldUseIAP();
  const webCrypto = !isNativeApp;
  const [showConfirm, setShowConfirm] = useState(false);
  const toast = useToast();
  const isIOSNative = iapService.shouldUseIAP();

  // Calculate current amount (either from preset or custom)
  const getCurrentAmount = () => {
    if (customAmount && parseFloat(customAmount) >= 1) {
      const usd = parseFloat(customAmount);
      return { usd, coins: usd * 1000, cash: usd };
    }
    if (selectedPreset !== null) {
      return PRESET_AMOUNTS[selectedPreset];
    }
    return PRESET_AMOUNTS[1]; // fallback
  };

  const currentAmount = getCurrentAmount();

  const handleSelectPreset = (index: number) => {
    setSelectedPreset(index);
    setCustomAmount(''); // Clear custom when preset selected
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    if (value && parseFloat(value) >= 1) {
      setSelectedPreset(null); // Deselect preset when custom entered
    }
  };

  const handleContinue = () => {
    // iOS IAP only supports preset amounts
    if (isIOSNative && selectedPreset === null) {
      toast({
        title: 'Select an Amount',
        description: 'Please select one of the preset amounts.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    if (currentAmount.usd < 1) {
      toast({
        title: 'Invalid Amount',
        description: 'Minimum purchase is $1.00',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    if (currentAmount.usd > 500) {
      toast({
        title: 'Amount Too Large',
        description: 'Maximum purchase is 500 USD',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    setShowConfirm(true);
  };

  const handlePurchase = async () => {
    const currentUser = auth.currentUser;

    if (purchasesPaused) {
      toast({ title: 'Purchases are paused', description: paymentsHold.message, status: 'warning', duration: 6000, isClosable: true });
      return;
    }

    if (!currentUser) {
      toast({
        title: 'Not Logged In',
        description: 'Please log in to make a purchase',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // iOS native: Use Apple In-App Purchase
      if (iapService.shouldUseIAP()) {
        const result = await iapService.purchasePlayCoins(currentAmount.usd);

        if (!result.success) {
          if (result.error === 'Purchase cancelled') {
            // User cancelled - no error toast needed
            return;
          }
          throw new Error(result.error || 'Purchase failed');
        }

        toast({
          title: 'Purchase Successful!',
          description: `${currentAmount.coins.toLocaleString()} PlayCoins + ${currentAmount.cash} PSS Coins credited!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setShowConfirm(false);
        return;
      }

      // Web & Android: Use Stripe
      const result = await stripeService.purchasePlayCoins(currentUser.uid, currentAmount.usd);

      if (!result.success) {
        throw new Error(result.error || 'Payment failed');
      }

      toast({
        title: 'Opening the payment page...',
        description: 'Finish the payment there. Your coins are added here automatically.',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Purchase error:', error);
      toast({
        title: 'Purchase Error',
        description: error instanceof Error ? error.message : 'Failed to process payment',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Amount Selection
  if (!showConfirm) {
    return (
      <Container 
        maxW="lg" 
        py={{ base: 4, md: 8 }} 
        px={{ base: 3, md: 6 }}
        pb={{ base: 24, md: 8 }}
      >
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading color={POKER_COLORS.cyan} size={{ base: 'md', md: 'lg' }} mb={2}>
              Get PlayCoins
            </Heading>
            <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>
              Purchase PlayCoins + Get FREE PSS Coins bonus *
            </Text>
          </Box>

          {/* Custom Amount Input */}
          <Box textAlign="center" py={{ base: 2, md: 4 }}>
            <HStack justify="center" spacing={3}>
              <Box bg={`${POKER_COLORS.cyan}22`} p={2} borderRadius="md">
                <Coins color={POKER_COLORS.cyan} size={24} />
              </Box>
              <Input
                type="number"
                placeholder={String(currentAmount.cash)}
                value={customAmount}
                onChange={(e) => handleCustomAmountChange(e.target.value)}
                textAlign="center"
                fontSize={{ base: '3xl', md: '5xl' }}
                fontWeight="bold"
                fontFamily="mono"
                color="white"
                bg="transparent"
                border="none"
                _focus={{ boxShadow: 'none' }}
                _placeholder={{ color: 'white' }}
                w={{ base: '120px', md: '150px' }}
                min={1}
                max={500}
              />
            </HStack>
            <Box 
              h="3px" 
              bg={`linear-gradient(90deg, ${POKER_COLORS.darkRed}, ${POKER_COLORS.gold})`}
              mt={2}
              mx="auto"
              w="80%"
              borderRadius="full"
            />
            <Text color="gray.500" fontSize="xs" mt={2}>
              Min: 1 USD | Max: 500 USD
            </Text>
          </Box>

          {/* "or select amount" */}
          <Text textAlign="center" color="gray.500" fontSize={{ base: 'xs', md: 'sm' }}>
            or select amount
          </Text>

          {/* FIXED: Preset Amount Buttons - 2x2 on mobile, 4 columns on desktop */}
          <Grid 
            templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }} 
            gap={{ base: 2, md: 3 }}
          >
            {PRESET_AMOUNTS.map((preset, index) => (
              <Box
                key={preset.usd}
                onClick={() => handleSelectPreset(index)}
                cursor="pointer"
                bg={selectedPreset === index && !customAmount ? `${POKER_COLORS.darkRed}33` : POKER_COLORS.darkBase}
                border="2px solid"
                borderColor={selectedPreset === index && !customAmount ? POKER_COLORS.darkRed : 'transparent'}
                borderRadius="xl"
                p={{ base: 2, md: 3 }}
                textAlign="center"
                transition="all 0.2s"
                _hover={{
                  borderColor: POKER_COLORS.darkRed,
                  transform: 'scale(1.02)',
                }}
                position="relative"
              >
                {/* Top: PlayCoins amount (what you GET) */}
                <HStack justify="center" spacing={1} mb={1}>
                  <Coins size={14} color={POKER_COLORS.cyan} />
                  <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>
                    {preset.coins.toLocaleString()}
                  </Text>
                </HStack>
                <Text color="gray.500" fontSize="xs" mb={1}>PlayCoins</Text>

                {/* Dashed divider */}
                <Box
                  borderTop="2px dashed"
                  borderColor={POKER_COLORS.darkRed}
                  my={2}
                  mx={{ base: -2, md: -3 }}
                  position="relative"
                >
                  {/* Circle connectors */}
                  <Box
                    position="absolute"
                    left="-6px"
                    top="-6px"
                    w={{ base: '8px', md: '10px' }}
                    h={{ base: '8px', md: '10px' }}
                    bg={POKER_COLORS.darkBase}
                    borderRadius="full"
                    border="2px solid"
                    borderColor={selectedPreset === index && !customAmount ? POKER_COLORS.darkRed : POKER_COLORS.darkTeal}
                  />
                  <Box
                    position="absolute"
                    right="-6px"
                    top="-6px"
                    w={{ base: '8px', md: '10px' }}
                    h={{ base: '8px', md: '10px' }}
                    bg={POKER_COLORS.darkBase}
                    borderRadius="full"
                    border="2px solid"
                    borderColor={selectedPreset === index && !customAmount ? POKER_COLORS.darkRed : POKER_COLORS.darkTeal}
                  />
                </Box>

                {/* Bottom: PSS Coins bonus (FREE with purchase) */}
                <Text color="gray.500" fontSize="xs" mt={1}>+ FREE Bonus</Text>
                <HStack justify="center" spacing={1} mt={1}>
                  <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                    {preset.cash} PSS Coins
                  </Text>
                </HStack>
              </Box>
            ))}
          </Grid>

          {/* Warning */}
          <Box 
            p={{ base: 3, md: 4 }}
            bg={`${POKER_COLORS.darkTeal}66`} 
            borderRadius="lg"
            borderLeft="4px solid"
            borderColor={POKER_COLORS.gold}
          >
            <Text color="gray.300" fontSize={{ base: 'xs', md: 'sm' }} lineHeight="1.6">
               <strong>Warning:</strong> Making two purchases of the same size within a 24 hour 

              period may cause the purchase to fail.
            </Text>
          </Box>

          {/* Legal Policy */}
          <Box>
            <HStack mb={2}>
              <Text color={POKER_COLORS.darkRed} fontWeight="bold">*</Text>
              <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                PSS Coins bonus policy
              </Text>
            </HStack>
            <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }} lineHeight="1.7">
              In order to get a PSS Coins bonus, you must purchase PlayCoins 
              ($1 USD per 1,000 coins). Check out the full information and rules for 
              the details and eligibility.
            </Text>
          </Box>

          {/* Continue Button */}
          <PokerButton
            variant="primary"
            w="100%"
            h={{ base: '48px', md: '56px' }}
            fontSize={{ base: 'md', md: 'lg' }}
            onClick={handleContinue}
            isLoading={loading}
            bg={`linear-gradient(135deg, ${POKER_COLORS.gold} 0%, ${POKER_COLORS.darkRed} 100%)`}
            _hover={{
              opacity: 0.9,
              transform: 'scale(1.01)',
            }}
          >
            Spend USD {currentAmount.usd.toFixed(2)}
          </PokerButton>
        </VStack>
      </Container>
    );
  }

  // Step 2: Confirmation & Payment
  return (
    <Container 
      maxW="lg" 
      py={{ base: 4, md: 8 }} 
      px={{ base: 3, md: 6 }}
      pb={{ base: 24, md: 8 }}
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading color={POKER_COLORS.cyan} size={{ base: 'md', md: 'lg' }} mb={2}>
            Submit purchase
          </Heading>
          <Text 
            color={POKER_COLORS.gold} 
            fontSize={{ base: 'lg', md: 'xl' }}
            fontWeight="bold"
            bgGradient={`linear(to-r, ${POKER_COLORS.gold}, ${POKER_COLORS.darkRed})`}
            bgClip="text"
          >
            Your bundle was created!
          </Text>
        </Box>

        {/* Bundle Card */}
        <Box
          bg={POKER_COLORS.darkBase}
          border="3px solid"
          borderColor={POKER_COLORS.darkRed}
          borderRadius="2xl"
          overflow="hidden"
          position="relative"
        >
          {/* PSS Coins Section (Top) */}
          <VStack py={{ base: 6, md: 8 }} spacing={2}>
            <Text color="white" fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold" fontFamily="mono">
              {currentAmount.cash.toFixed(2)}
            </Text>
            <HStack>
              <DollarSign size={18} color={POKER_COLORS.gold} />
              <Text color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>
                PSS Coins bonus
              </Text>
            </HStack>
          </VStack>

          {/* Dashed Divider with circles */}
          <Box position="relative" py={2}>
            <Box
              borderTop="3px dashed"
              borderColor={POKER_COLORS.darkRed}
              mx={4}
            />
            {/* Left circle cutout */}
            <Box
              position="absolute"
              left="-12px"
              top="50%"
              transform="translateY(-50%)"
              w="24px"
              h="24px"
              bg="#0D1B2A"
              borderRadius="full"
              border="3px solid"
              borderColor={POKER_COLORS.darkRed}
            />
            {/* Right circle cutout */}
            <Box
              position="absolute"
              right="-12px"
              top="50%"
              transform="translateY(-50%)"
              w="24px"
              h="24px"
              bg="#0D1B2A"
              borderRadius="full"
              border="3px solid"
              borderColor={POKER_COLORS.darkRed}
            />
          </Box>

          {/* PlayCoins Section (Bottom) */}
          <VStack py={{ base: 4, md: 6 }} spacing={2}>
            <Text color="white" fontSize={{ base: '3xl', md: '4xl' }} fontWeight="bold" fontFamily="mono">
              {currentAmount.coins.toLocaleString()}
            </Text>
            <HStack>
              <Coins size={18} color={POKER_COLORS.gold} />
              <Text color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>
                PlayCoins purchase
              </Text>
            </HStack>
          </VStack>
        </Box>

        {/* Playthrough Notice */}
        <Box
          p={{ base: 3, md: 4 }}
          bg={`${POKER_COLORS.cyan}11`}
          borderRadius="lg"
          borderLeft="4px solid"
          borderColor={POKER_COLORS.cyan}
        >
          <HStack align="start" spacing={3}>
            <Info size={20} color={POKER_COLORS.cyan} />
            <Box>
              <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize={{ base: 'xs', md: 'sm' }}>
                1x Playthrough Required
              </Text>
              <Text color="gray.400" fontSize={{ base: '2xs', md: 'xs' }} mt={1}>
                Play through ${currentAmount.cash.toFixed(2)} in games to unlock PSS Coins redemption.
              </Text>
            </Box>
          </HStack>
        </Box>

        {/* Temporary web rail notice: crypto only, until a card processor is live */}
        {webCrypto && !purchasesPaused && (
          <Box p={3} bg="rgba(0, 217, 217, 0.06)" border="1px solid rgba(0, 217, 217, 0.35)" borderRadius="lg">
            <Text color="white" fontSize="sm" fontWeight="bold" mb={1}>Card payments are temporarily unavailable</Text>
            <Text color="gray.300" fontSize="xs" lineHeight="1.5">
              While we bring a new payment provider on board, PlayCoins can be purchased with USDC and other
              cryptocurrencies through our payment partner. This is a temporary option and card payments will return shortly.
              Purchases are for PlayCoins only. PSS Coins remain a free bonus and are never sold. No purchase is necessary to
              play or win. <Text as="a" href="/legal?tab=amoe" color={POKER_COLORS.cyan} textDecoration="underline">See Free Entry</Text>.
            </Text>
          </Box>
        )}

        {/* Payment Button. Hidden while the admin payments hold is on. */}
        <VStack spacing={3}>
          {androidStoreApp ? (
            <Alert status="info" bg="rgba(0, 217, 217, 0.08)" border="1px solid rgba(0, 217, 217, 0.35)" borderRadius="lg" alignItems="flex-start">
              <AlertIcon color={POKER_COLORS.cyan} />
              <AlertDescription color="white" fontSize="sm">
                <Text fontWeight="bold" mb={1}>Purchases are temporarily unavailable in this app</Text>
                Free play and your balances are unaffected. Thanks for your patience.
              </AlertDescription>
            </Alert>
          ) : purchasesPaused ? (
            <Alert status="warning" bg="rgba(249, 115, 22, 0.15)" border="1px solid #fb923c" borderRadius="lg" alignItems="flex-start">
              <AlertIcon color="#fb923c" />
              <AlertDescription color="white" fontSize="sm">
                <Text fontWeight="bold" mb={1}>Purchases are paused right now</Text>
                {paymentsHold.message}
              </AlertDescription>
            </Alert>
          ) : (
            <PokerButton
              variant="primary"
              w="100%"
              h={{ base: '48px', md: '56px' }}
              fontSize={{ base: 'md', md: 'lg' }}
              onClick={handlePurchase}
              isLoading={loading}
              leftIcon={<CreditCard size={20} />}
            >
              {loading ? <Spinner size="sm" /> : (webCrypto ? 'Pay with crypto (USDC and more)' : 'Credit/Debit Card')}
            </PokerButton>
          )}
        </VStack>

        {/* Back Button */}
        <Box textAlign="center">
          <Text
            color={POKER_COLORS.cyan}
            fontSize="sm"
            cursor="pointer"
            onClick={() => setShowConfirm(false)}
            _hover={{ textDecoration: 'underline' }}
          >
            ← Change amount
          </Text>
        </Box>

        {/* Legal Footer */}
        <Box p={{ base: 3, md: 4 }} bg={`${POKER_COLORS.darkTeal}44`} rounded="lg" textAlign="center">
          <Text color="gray.500" fontSize={{ base: '2xs', md: 'xs' }} lineHeight="1.6">
             Secure payment through our payment partner. PlayCoins are virtual currency for entertainment only. 

            PSS Coins are redeemable after playthrough requirements. 18+ only. 
            Please play responsibly.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default BuyCoinsPage;